import { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { AlertCircle, ArrowLeft } from 'lucide-react'
import { GET_CART_BY_ID } from '../B2BCartsQueries'
import { PageContainer } from '@/components/shared/page-container'
import { HeaderSection } from '@/components/shared/header-section'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatDate } from '@/utils/dateUtils'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'EUR',
})

const LoadingSkeleton = () => (
  <PageContainer>
    <Card className="p-6">
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
      </div>
    </Card>
  </PageContainer>
)

const ErrorDisplay = ({ message }: { message: string }) => (
  <PageContainer>
    <Card className="p-6">
      <div className="flex items-center text-red-500 gap-2">
        <AlertCircle className="h-5 w-5" />
        <span>Error loading cart: {message}</span>
      </div>
    </Card>
  </PageContainer>
)

export default function CartDetails() {
  const { accountId, cartId } = useParams()
  const navigate = useNavigate()

  const { data, loading, error } = useQuery(GET_CART_BY_ID, {
    variables: { cartId: Number(cartId) },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  })

  const cart = useMemo(() => data?.getCartById, [data])

  if (loading) return <LoadingSkeleton />
  if (error) return <ErrorDisplay message={error.message} />
  if (!cart) {
    return (
      <PageContainer>
        <Card className="p-6">
          <div className="flex items-center text-gray-500 gap-2">
            <AlertCircle className="h-5 w-5" />
            <span>Cart not found</span>
          </div>
        </Card>
      </PageContainer>
    )
  }

  const items = cart.items || []
  const totalVolume = items.reduce(
    (sum: number, item: any) => sum + (item.volume || 0),
    0
  )

  return (
    <PageContainer>
      <Button
        variant="link"
        className="p-0 h-auto mb-2 text-xs text-blue-500"
        onClick={() =>
          navigate(`/dashboard/b2b-accounts/${accountId}/carts`)
        }
      >
        <ArrowLeft className="h-3 w-3 mr-1" />
        Back to carts
      </Button>

      <HeaderSection
        title={cart.name || 'Untitled cart'}
        subtitle={`Updated ${formatDate(cart.updatedAt)}`}
      />
      <div className="flex flex-wrap items-center gap-2 mb-8">
        {cart.isActive && (
          <Badge variant="green" className="text-xs">
            Active
          </Badge>
        )}
        {cart.isModified && (
          <Badge variant="lightBlue" className="text-xs">
            Modified
          </Badge>
        )}
        <span className="text-sm text-muted-foreground">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          Products
        </CardHeader>
        <CardContent>
          <Table className="text-gray-500 text-xs leading-6">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Image</TableHead>
                <TableHead>Product Name</TableHead>
                <TableHead className="text-right">Reference</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Volume</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center text-gray-400 py-8"
                  >
                    This cart is empty.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item: any, index: number) => {
                  const container = item.product_container

                  if (!container) {
                    return (
                      <TableRow key={item.id ?? index}>
                        <TableCell className="p-2">
                          <div className="w-[60px] h-[60px] bg-gray-100 rounded-md flex items-center justify-center">
                            <span className="text-gray-400 text-xs">
                              No image
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium p-2">
                          <span className="text-gray-400">Deleted Product</span>
                        </TableCell>
                        <TableCell className="text-right p-2">
                          <span className="text-gray-400">
                            {item.reference}
                          </span>
                        </TableCell>
                        <TableCell className="text-right p-2">
                          {item.quantity || 1}
                        </TableCell>
                        <TableCell className="text-right p-2">
                          {item.price != null
                            ? currencyFormatter.format(item.price)
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right p-2">
                          {item.volume?.toFixed(2)} m<sup>3</sup>
                        </TableCell>
                      </TableRow>
                    )
                  }

                  const profiles =
                    container.type === 'SINGLE_PRODUCT'
                      ? container.single_product?.product_profiles
                      : container.advanced_product?.advanced_product_profiles
                  const images =
                    container.type === 'SINGLE_PRODUCT'
                      ? container.single_product?.images
                      : container.advanced_product?.images
                  const productName = profiles?.[0]?.name || 'Unnamed Product'
                  const thumbnail = images?.[0]?.src_thumbnail

                  const fabricNames: string[] = (item.cartItemFabrics || [])
                    .map(
                      (cif: any) => {
                        const live = cif.fabric?.color_name
                        const snap = cif.fabric_color_name
                        const name = snap || live || cif.fabric_code || cif.fabric?.code
                        return snap && live && snap !== live
                          ? `${snap} (${live})`
                          : name
                      }
                    )
                    .filter(Boolean)

                  const componentNames: string[] = (
                    item.additional_components || []
                  )
                    .map(
                      (c: any) =>
                        c.additional_component_profiles?.[0]?.name || c.code
                    )
                    .filter(Boolean)

                  return (
                    <TableRow key={item.id ?? index}>
                      <TableCell className="p-2">
                        {thumbnail ? (
                          <img
                            src={thumbnail}
                            alt={productName}
                            className="w-[60px] h-[60px] object-cover rounded-md"
                          />
                        ) : (
                          <div className="w-[60px] h-[60px] bg-gray-100 rounded-md flex items-center justify-center">
                            <span className="text-gray-400 text-xs">
                              No image
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium p-2">
                        <div className="flex flex-col gap-1">
                          <span>{productName}</span>
                          {fabricNames.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {fabricNames.map((name, i) => (
                                <Badge
                                  key={i}
                                  variant="outline"
                                  className="text-xs font-normal"
                                >
                                  {name}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {componentNames.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {componentNames.map((name, i) => (
                                <Badge
                                  key={i}
                                  variant="secondary"
                                  className="text-xs font-normal"
                                >
                                  {name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right p-2">
                        {item.reference}
                      </TableCell>
                      <TableCell className="text-right p-2">
                        {item.quantity || 1}
                      </TableCell>
                      <TableCell className="text-right p-2">
                        {item.price != null
                          ? currencyFormatter.format(item.price)
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right p-2">
                        {item.volume?.toFixed(2)} m<sup>3</sup>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
              {items.length > 0 && (
                <TableRow className="border-t-2">
                  <TableCell colSpan={5} className="text-right font-medium p-2">
                    Total Volume:
                  </TableCell>
                  <TableCell className="text-right font-medium p-2">
                    {totalVolume.toFixed(2)} m<sup>3</sup>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
